import { writesApi } from './api';

// ── Ops settlement of stuck transactions ───────────────────────────────────
// A basket can be paid for and delivered yet never confirmed by the buyer. Its
// escrow then sits unreleased forever: the merchant, the rider, Chekit's
// commission and the VAT float are never paid. This endpoint runs the SAME
// release path a buyer confirmation runs, with the operator's identity and
// reason stamped onto the money movement.
//
// Spring Boot writes (:8080). Gated on chekit-level `payments.process`.

/** The goods half of the split the completion path computed. */
export interface CompletionGoods {
  subtotal: number;
  exVatBase: number;
  embeddedVat: number;
  commissionMode: string | null;
  commissionRate: number;
  commissionAmount: number;
  merchantNet: number;
}

/** `CommissionService.DeliverySplit.snapshot()` — the delivery half. */
export interface CompletionDelivery {
  fee: number;
  taxModel: string | null;
  commissionRate: number | null;
  vatRate: number | null;
  vatPayer: string | null;
  vat: number;
  chekitCommission: number;
  driverPay: number;
}

export interface CompletionAllocations {
  merchant: number;
  commission: number;
  rider: number;
  tax: number;
  total: number;
  /** True when no rider wallet could be resolved, so the rider share was folded
   *  into commission instead of being dropped. */
  riderFoldedIntoCommission: boolean;
}

export interface CompletionSplit {
  goods: CompletionGoods;
  delivery: CompletionDelivery;
  allocations: CompletionAllocations;
  reconciles: boolean;
}

/** Which rider is being paid — null when no rider is attached to the basket. */
export interface CompletionRider {
  riderId: string;
  userId: string | null;
  name: string | null;
}

export interface CompletionActor {
  userId: string | null;
  name: string | null;
  reason: string | null;
}

export interface CompleteBasketResult {
  /** True → nothing moved, this was a dry run. */
  preview: boolean;
  basketId: string;
  orderId: string | null;
  basketStatus: string;
  orderStatus: string | null;
  paymentStatus: string | null;
  totalAmount: number;
  split: CompletionSplit;
  rider: CompletionRider | null;
  /** Only set on a real (non-preview) completion. */
  journalEntryId: string | null;
  completedBy: CompletionActor | null;
}

export interface CompleteBasketRequest {
  /** Mandatory — an unexplained money movement cannot be audited later. */
  reason: string;
  preview?: boolean;
}

export const settlementService = {
  /** Release a stuck-but-paid basket. Always call with `preview: true` first —
   *  the returned split is what the real call will post.
   *
   *  Rejects with the API's own message on ORDER_NOT_PAID (409),
   *  ALREADY_RELEASED (409), BASKET_TERMINAL (409), BASKET_NOT_FOUND (404). */
  completeBasket: (basketId: string, body: CompleteBasketRequest) =>
    writesApi.post<CompleteBasketResult>(
      `/admin/settlements/baskets/${basketId}/complete`,
      { reason: body.reason, preview: body.preview ?? false },
    ),
};
